<template>
  <div
    class="editor-page-drawer"
    :class="{ 'editor-page-drawer--scroll-animation': scrollAnimation }"
  >
    <div
      v-if="hasHeader"
      ref="editorPageDrawerHeader"
      class="editor-page-drawer__header"
      :class="{ 'editor-page-drawer__header--fixed': headerIsFixed }"
    >
      <slot name="header" />
    </div>
    <div
      v-for="(item, index) in pageGroups"
      :key="item.sectionId"
      class="editor-page-drawer__accordion-wrap"
    >
      <NebulaAccordion
        ref="accordions"
        :data-section-id="item.sectionId"
        class="editor-page-drawer__accordion"
        :class="{ 'editor-page-drawer__accordion--header-fixed': hasHeader && headerIsFixed }"
        mode="flat"
        is-expanded-on-load
        :button-data-attributes="{
          disabled: !groupsAreCollapsable,
          tabindex: isDisabled ? -1 : 0,
        }"
      >
        <template #header>
          <div class="editor-page-drawer__section-header">
            <div
              v-if="item.isSection"
              class="editor-page-drawer__section-number"
              de-tracking-name="studio_lesson-sidebar_section-header-button_open"
            >
              {{ $t('Section %(number)d', { number: index + 1 }) }}
            </div>
            <div class="editor-page-drawer__section-name-wrap">
              <NebulaIcon
                class="editor-page-drawer__section-icon"
                size="s"
                :symbol-id="item.section.icon || 'information'"
              />
              <span class="editor-page-drawer__section-name">
                {{ item.section.name }}
              </span>
              <NebulaIcon
                v-if="groupsAreCollapsable"
                class="editor-page-drawer__section-caret"
                de-tracking-name="studio_lesson-sidebar_section-header-button_close"
                size="s"
                symbol-id="caret-down"
              />
            </div>
          </div>
        </template>
        <div class="editor-page-drawer__pages">
          <button
            ref="pageButtons"
            :id="`editor-page-drawer-page-button-${page.id}`"
            class="editor-page-drawer__page-button"
            :class="{
              'editor-page-drawer__page-button--current': isCurrentPage(page),
            }"
            v-for="page in item.pages"
            :key="page.id"
            :aria-label="pageAriaLabel(item, page.index)"
            @click="onPageButtonClick(page)"
            :tabindex="isDisabled ? -1 : 0"
          >
            <div
              class="editor-page-drawer__page-icons"
            >
              <div class="editor-page-drawer__page-icons-wrap">
                <NebulaIcon
                  v-if="pageHasQuestion(page)"
                  symbol-id="circle-question"
                  size="s"
                />
                <NebulaIcon
                  v-if="pageHasVideo(page) || pageHasAudio(page)"
                  symbol-id="circle-play"
                  size="s"
                />
              </div>
              <div
                class="editor-page-drawer__page-icons-wrap
                  editor-page-drawer__page-icons-wrap--filled"
              >
                <NebulaIcon
                  v-if="pageHasQuestion(page)"
                  symbol-id="circle-question--filled"
                  size="s"
                />
                <NebulaIcon
                  v-if="pageHasVideo(page) || pageHasAudio(page)"
                  symbol-id="circle-play--filled"
                  size="s"
                />
              </div>
              <span class="editor-page-drawer__page-number">
                {{ page.index + 1 }}
              </span>
            </div>
            <div
              class="editor-page-drawer__page-image-wrap"
            >
              <EditorPageDrawerThumbnail :url="pageThumbnailUrl(page)" />
            </div>
          </button>
        </div>
      </NebulaAccordion>
    </div>
  </div>
</template>

<script>
import { last } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { v4 as uuid } from 'uuid';
import {
  NebulaAccordion,
  NebulaIcon,
} from '@discoveryedu/nebula-components';
import EditorPageDrawerThumbnail from '@/components/EditorPageDrawerThumbnail.vue';
import pageModuleTypes from '@/mixins/pageModuleTypes';
import {
  useEditorStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'EditorPageDrawer',
  components: {
    NebulaAccordion,
    NebulaIcon,
    EditorPageDrawerThumbnail,
  },
  mixins: [pageModuleTypes],
  data() {
    return {
      headerHeight: 0,
      scrollAnimation: false,
      scrollBehavior: 'center', // center, nearest
    };
  },
  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    headerIsFixed: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToCurrentPage(this.getDefaultScrollArgs());
      this.$nextTick(() => {
        // Start animation after initial page load
        this.scrollAnimation = true;
        this.attachHeaderObserver();
      });
    });
  },
  beforeUnmount() {
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }
  },
  watch: {
    currentPage() {
      this.onPageChanged();
    },
    headerIsFixed() {
      this.attachHeaderObserver();
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PAGE_BY_ID,
    ]),
    getDefaultScrollArgs() {
      return {
        scrollBehavior: this.scrollBehavior,
        scrollRect: this.$el.getBoundingClientRect(),
      };
    },
    onPageChanged() {
      const scrollArgs = this.getDefaultScrollArgs();
      // Always reset the scroll behavior to 'center' after page change.
      this.scrollBehavior = 'center';
      const group = this.pageGroups.find((g) => g.pages.find((p) => p.id === this.currentPage.id));
      let timeout = 0;
      if (group) {
        const accordion = this.$refs.accordions
          .find((a) => a.$el.getAttribute('data-section-id') === group.sectionId);
        if (accordion) {
          /*
            If we've changed to a page that is inside of a collapsed accordion, first
            expand the accordion, then scroll to the current selected page.
          */
          if (!accordion.isExpanded) {
            if (accordion.expand) {
              accordion.expand();
            } else {
              accordion.handleExpand();
            }
            timeout = 251; // Wait for accordion expand animation to complete before scrolling.
          }

          if (scrollArgs.scrollBehavior === 'nearest') {
            const accordionHeader = accordion.$el.querySelector('.nebula-accordion__button');
            if (accordionHeader) {
              const headerRect = accordionHeader.getBoundingClientRect();
              const {
                right, bottom, left, width, height,
              } = scrollArgs.scrollRect;
              scrollArgs.scrollRect = {
                top: headerRect.bottom,
                right,
                bottom,
                left,
                width,
                height: height - headerRect.height,
              };
            }
          }
        }
      }
      setTimeout(() => {
        this.scrollToCurrentPage(scrollArgs);
      }, timeout);
    },
    scrollToCurrentPage({ scrollBehavior, scrollRect }) {
      const pageId = `editor-page-drawer-page-button-${this.currentPage?.id}`;
      const pageButton = this.$refs.pageButtons.find((b) => b.id === pageId);
      if (pageButton) {
        const pageRect = pageButton.getBoundingClientRect();

        let targetTop;
        if (scrollBehavior === 'center') {
          /*
            If scrollBehavior is 'center', then always try to scroll the page
            to the center of the scroll area.
          */
          targetTop = scrollRect.top + scrollRect.height / 2 - pageRect.height / 2;
        } else {
          /*
            If scrollBehavior is 'nearest', only scroll if the page is out of view
            or partially out of view. Scrolls the page to the nearest edge of
            the scroll area, just enough to make it visible.
          */
          if (pageRect.top > scrollRect.top && pageRect.bottom < scrollRect.bottom) {
            return;
          }
          const pageGap = 2;
          if (pageRect.top <= scrollRect.top) {
            targetTop = scrollRect.top + pageGap;
          }
          if (pageRect.bottom >= scrollRect.bottom) {
            targetTop = scrollRect.bottom - pageRect.height - pageGap;
          }
        }

        const distance = pageRect.top - targetTop;
        const { scrollTop, scrollHeight } = this.$el;
        let scrollTo = scrollTop + distance;
        if (scrollTo < 0) scrollTo = 0;
        if (scrollTo > scrollHeight) scrollTo = scrollHeight;
        this.$el.scrollTop = scrollTo;
      }
    },
    pageAriaLabel(pageGroup, index) {
      if (this.hasSections) {
        return this.$t('%(sectionName)s - Page %(pageNumber)s', {
          pageNumber: index + 1,
          sectionName: pageGroup.section.name,
        });
      }
      return this.$t('Page %(pageNumber)s', { pageNumber: index + 1 });
    },
    pageThumbnailUrl(page) {
      return page?.thumbnail?.url;
    },
    isCurrentPage(page) {
      return this.currentPage?.id === page.id;
    },
    onPageButtonClick(page) {
      if (this.currentPage?.id === page.id) return;
      /*
        When a user explicitly clicks/selects a page in the page drawer,
        only scroll the page if it is out of view. All other page navigations
        (e.g. slide arrows, arrow keys, etc) will use scroll behavior 'center'.
      */
      this.scrollBehavior = 'nearest';
      this[types.VIEW_PAGE_BY_ID](page.id);
    },
    attachHeaderObserver() {
      const headerRef = this.$refs.editorPageDrawerHeader;
      if (!headerRef) return;

      if (this.headerResizeObserver) {
        this.headerResizeObserver.disconnect();
      }

      this.headerResizeObserver = new ResizeObserver(() => {
        const adjustHeaderHeight = 1;
        if (headerRef) {
          const headerRect = headerRef.getBoundingClientRect();
          this.headerHeight = Math.round(headerRect.height - adjustHeaderHeight);
        }
      });

      if (headerRef) {
        this.headerResizeObserver.observe(headerRef);
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'groupedPages',
      'currentPage',
      'draftType',
    ]),
    hasHeader() {
      return !!this.$slots.header;
    },
    pageGroups() {
      return this.groupedPages.reduce((acc, item) => {
        if (item.isSection) {
          acc.push(item);
        } else {
          const lastGroup = last(acc);
          if (lastGroup?.isPageGroup) {
            lastGroup.pages.push(item);
          } else {
            acc.push({
              isPageGroup: true,
              pages: [item],
              sectionId: uuid(),
              section: {
                icon: 'studio-slideshow',
                name: this.draftType === 'board'
                  ? this.$t('Pages')
                  : this.$t('Slides'),
              },
            });
          }
        }
        return acc;
      }, []);
    },
    hasSections() {
      return this.pageGroups.some((g) => g.isSection);
    },
    groupsAreCollapsable() {
      return this.hasSections && this.pageGroups.length > 1;
    },
    headerHeightStyle() {
      return `${this.headerHeight}px`;
    },
  },
};
</script>

<style lang="stylus">
.editor-page-drawer {
  height: 100%;
  overflow-y: auto;
  width: 100%;

  &--scroll-animation {
    scroll-behavior: smooth;
  }

  &__section-header {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    gap: $nebula-space-1x;
    width: 100%;
  }

  &__header {
    position: relative;

    &--fixed {
      position: sticky;
      top: 0;
      z-index: 2; // Above page drawer accordion headers
    }
  }

  &__section-number {
    color: $nebula-text-color-header-default;
    font-size: $nebula-font-size-caption;
    font-weight: 600;
  }

  &__section-name-wrap {
    align-items: flex-start;
    display: flex;
    gap: $nebula-space-1x;
    width: 100%;
  }

  &__section-icon {
    fill: $nebula-text-color-header-default;
    flex-shrink: 0;
  }

  &__section-name {
    color: $nebula-text-color-header-default;
    flex: 1 1 auto;
    font-size: $nebula-font-size-caption;
    font-weight: 600;
    text-align: left;
  }

  &__section-caret {
    fill: $nebula-text-color-header-default;
    flex-shrink: 0;
    transition: transform $nebula-transition-fade-in;
  }

  &__accordion {
    --nebula-accordion-border-radius: 0;
    --nebula-accordion-button-background-color: $nebula-color-neutral-100;
    --nebula-accordion-button-text-color: $nebula-text-color-header-default;
    --nebula-accordion-caret-color: $nebula-text-color-header-default;
    --nebula-accordion-icon-color: $nebula-text-color-header-default;
    --nebula-accordion-expanded-background-color: $nebula-color-white;
    --nebula-accordion-expanded-color: $nebula-text-color-header-default;
    --nebula-accordion-non-outlined-background-color: $nebula-color-white;
    margin: 0;

    > div {
      &:first-child {
        background-color: $nebula-color-white;
        position: sticky;
        top: 0;
        z-index: 1;
      }
    }
  }

  &__accordion--header-fixed {
    > div {
      &:first-child {
        top: v-bind(headerHeightStyle);
      }
    }
  }

  &__pages {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 2px;
  }

  &__page-button {
    align-items: stretch;
    background-color: $nebula-color-white;
    border: 0;
    border-radius: $nebula-border-radius-small;
    display: flex;
    gap: $nebula-space-half;
    padding: $nebula-space-1x $nebula-space-1x $nebula-space-1x $nebula-space-half;
    transition: $nebula-transition-default;
    width: 100%;

    &:hover:not(.editor-page-drawer__page-button--current) {
      background-color: $nebula-color-platform-interactive-200;

      .editor-page-drawer__page-image-wrap {
        box-shadow: none;
      }
    }

    &--current {
      background-color: $nebula-color-platform-interactive-300;
    }
  }

  &__page-number {
    color: $nebula-color-platform-interactive-900;
    font-size: $nebula-font-size-caption;
    font-weight: 600;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 100%;
  }

  &__page-image-wrap {
    background-color: $nebula-color-white;
    border-radius: $nebula-border-radius-small;
    box-shadow: 0 0 0 1px $nebula-color-platform-interface-300;
    flex: 1 1 auto;
    padding: 6px;
    transition: $nebula-transition-default;

    .editor-page-drawer__page-button--current & {
      box-shadow: 0 0 0 2px $nebula-color-platform-interactive-800;
    }
  }

  &__page-icons {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    position: relative;
    width: $nebula-space-2x;

    svg {
      fill: $nebula-color-platform-interactive-900;
    }
  }

  &__page-icons-wrap {
    display: flex;
    flex-direction: column;
    gap: 2px;
    opacity: 1;
    position: relative;
    top: 0;
    transition: $nebula-transition-default;

    &--filled {
      opacity: 0;
    }

    &:not(:first-child) {
      transform: translateY(-100%);
    }

    .editor-page-drawer__page-button--current & {
      opacity: 0;

      &--filled {
        opacity: 1;
      }
    }
  }

  &__accordion-wrap {
    &:not(:last-child) {
      .nebula-accordion__content {
        border-bottom: 1px solid $nebula-color-platform-interface-300;

        &--hidden {
          border-bottom: 0;
        }
      }
    }
  }

  .nebula-accordion__button {
    border-bottom: 1px solid $nebula-color-platform-interface-300;
    border-left: 2px solid transparent;
    border-right: 2px solid transparent;
    border-top: 2px solid transparent;

    @media only screen and (max-width: 600px) {
      padding: $nebula-space-1x $nebula-space-2x 12px;
    }

    &--expanded {
      border-bottom: 1px solid transparent;

      .editor-page-drawer__section-caret {
        transform: rotate(180deg);
      }
    }

    &:not(:disabled):hover {
      background-color: $nebula-color-platform-interactive-100;
    }
  }

  .nebula-accordion__header {
    width: 100%;
  }

  .nebula-accordion-caret {
    display: none;
  }
}
</style>
